/**
 * Project Filters Component
 * Type chips, search, and sort controls
 */

import { useState, useEffect, useRef } from 'react';
import { Search, Archive } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { ProjectType, ProjectListParams } from '../types';

interface ProjectFiltersProps {
  params: ProjectListParams;
  onParamsChange: (params: Partial<ProjectListParams>) => void;
}

const PROJECT_TYPES: { value: ProjectType | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'installation', label: 'Installation' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'calibration', label: 'Calibration' },
  { value: 'replacement', label: 'Replacement' },
];

const SORT_OPTIONS = [
  { value: 'scheduled_date:asc', label: 'Scheduled First' },
  { value: 'scheduled_date:desc', label: 'Scheduled Last' },
  { value: 'created_at:desc', label: 'Newest' },
  { value: 'created_at:asc', label: 'Oldest' },
  { value: 'amount:desc', label: 'Amount (High)' },
  { value: 'amount:asc', label: 'Amount (Low)' },
];

const SEARCH_DEBOUNCE_MS = 300;

export function ProjectFilters({ params, onParamsChange }: ProjectFiltersProps) {
  // Local state for search input to enable debouncing
  const [searchValue, setSearchValue] = useState(params.search || '');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local search state when params change externally
  useEffect(() => {
    setSearchValue(params.search || '');
  }, [params.search]);

  const handleTypeChange = (type: ProjectType | 'all') => {
    onParamsChange({
      type: type === 'all' ? undefined : type,
      pageNumber: 0, // 0-indexed pagination
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounced search
    debounceTimerRef.current = setTimeout(() => {
      onParamsChange({
        search: value || undefined,
        pageNumber: 0, // 0-indexed pagination
      });
    }, SEARCH_DEBOUNCE_MS);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSortChange = (value: string) => {
    const [sortKey, sortDir] = value.split(':') as [string, 'asc' | 'desc'];
    // Convert string direction to numeric for API compatibility
    const sortDirection = sortDir === 'asc' ? 1 : -1;
    onParamsChange({
      sortKey,
      sortDirection,
    });
  };

  // Convert numeric direction back to string for dropdown display
  const sortDirStr = params.sortDirection === -1 ? 'desc' : 'asc';
  const currentSort = `${params.sortKey || 'scheduled_date'}:${sortDirStr}`;
  const currentType = params.type || 'all';

  return (
    <div className="space-y-3">
      {/* Type Chips */}
      <div className="flex flex-wrap gap-2">
        {PROJECT_TYPES.map((type) => (
          <Button
            key={type.value}
            variant={currentType === type.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleTypeChange(type.value)}
            className={cn(
              'rounded-full',
              currentType === type.value && 'shadow-sm'
            )}
          >
            {type.label}
          </Button>
        ))}
      </div>

      {/* Search and Sort */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={currentSort} onValueChange={handleSortChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
          <Checkbox
            checked={!!params.includeArchived}
            onCheckedChange={(checked) =>
              onParamsChange({
                includeArchived: checked ? true : undefined,
                pageNumber: 0,
              })
            }
          />
          <Archive className="h-3.5 w-3.5" />
          Archived
        </label>
      </div>
    </div>
  );
}
